import { unwrap } from "@sagot/api-client";
import { tenantNavFromServer, type TenantNavEntry } from "@sagot/constants";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../lib/query-keys";
import { useApi } from "../session/session-provider";
import { useWorkspaceId } from "../session/workspace-context";

/**
 * Keeps the last sidebar of the same workspace on screen while the nav for another agent loads
 * (the active agent resolves after first paint on workspace pages), so the links never blink out.
 */
export function keepSameWorkspaceNav<T>(wsId: string) {
  return (previous: T | undefined, previousQuery: { queryKey: readonly unknown[] } | undefined): T | undefined =>
    previousQuery?.queryKey[1] === wsId ? previous : undefined;
}

/** `GET /v1/nav?agent_id=`: the sidebar exactly as the server filtered it. */
export function useConsoleNav(agentId: string | null): { entries: TenantNavEntry[]; isPending: boolean; isError: boolean; refetch: () => void } {
  const api = useApi();
  const wsId = useWorkspaceId();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.nav(wsId, agentId),
    queryFn: () => unwrap(api.GET("/v1/nav", { params: { query: agentId ? { agent_id: agentId } : {} } })),
    staleTime: 30_000,
    select: tenantNavFromServer,
    placeholderData: keepSameWorkspaceNav(wsId),
  });
  // A retry refreshes every nav of the workspace (the sidebar's agent-scoped one too), not only this hook's key.
  const refetch = () => void queryClient.invalidateQueries({ queryKey: queryKeys.navAll(wsId) });
  return { entries: query.data ?? [], isPending: query.isPending, isError: query.isError, refetch };
}
